import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RAW_UNIT_SUFFIX, type RawItemDto, type RawStockMovementReason } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

interface RawStockAdjustDialogProps {
  rawItem: RawItemDto;
  onClose: () => void;
  onSaved: () => void;
}

/** The reasons a human enters by hand; OPENING and SALE belong to the system. */
const MANUAL_REASONS = [
  'PURCHASE',
  'WASTAGE',
  'ADJUSTMENT',
] as const satisfies readonly RawStockMovementReason[];

/**
 * Purchase / wastage / adjustment for a raw item — the raw twin of the
 * product `StockAdjustDialog`, with one deliberate difference: the result MAY
 * be negative and the form does not block it. The sale path is allowed to
 * overdraw the raw shelf, so the correction path must be able to as well —
 * refusing a wastage entry because the count on paper is already wrong would
 * make the wrong count permanent.
 */
export function RawStockAdjustDialog({ rawItem, onClose, onSaved }: RawStockAdjustDialogProps) {
  const { t } = useTranslation();

  const [delta, setDelta] = useState('1');
  const [reason, setReason] = useState<RawStockMovementReason>('PURCHASE');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const suffix = RAW_UNIT_SUFFIX[rawItem.unit];
  const parsed = Math.round(Number(delta)) || 0;
  const resulting = rawItem.stockQuantity + parsed;

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('store.rawItems.adjustStock')}</h2>
        <p className="mt-1 mb-4 text-sm text-content-secondary">
          {rawItem.name} ·{' '}
          {t('store.rawItems.inStock', { count: rawItem.stockQuantity, unit: suffix })}
        </p>

        <label className="block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('store.rawItems.delta', { unit: suffix })}
          </span>
          <input
            type="number"
            step="1"
            value={delta}
            onChange={(event) => setDelta(event.target.value)}
            required
            className={`${inputClass} tabular`}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('store.rawItems.deltaHint')}
          </span>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('store.rawItems.reason')}
          </span>
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value as RawStockMovementReason)}
            className={inputClass}
          >
            {MANUAL_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(`store.rawItems.reasons.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('store.rawItems.note')}
          </span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className={inputClass}
          />
        </label>

        <p className={`mt-3 text-sm ${resulting < 0 ? 'text-danger' : 'text-content-secondary'}`}>
          {t('store.rawItems.resultingStock', { count: resulting, unit: suffix })}
          {resulting < 0 ? ` — ${t('store.rawItems.goesNegative')}` : ''}
        </p>

        {error ? (
          <p role="alert" className="mt-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || parsed === 0}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/raw-items/${rawItem.id}/stock`, {
        delta: parsed,
        reason,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('store.rawItems.couldNotSave'));
    } finally {
      setSubmitting(false);
    }
  }
}
